import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SoundProvider } from "@/contexts/SoundContext";
import { AuthSessionProvider } from "@/contexts/AuthSessionContext";
import { PremiumProvider } from "@/contexts/PremiumContext";
import { TierProvider } from "@/contexts/TierContext";
import { DashboardDataProvider } from "@/contexts/DashboardDataContext";
import { AnimatePresence } from "framer-motion";
import ErrorBoundary from "@/components/ErrorBoundary";
import PageTransition from "@/components/PageTransition";
import SmartInstallPrompt from "@/components/SmartInstallPrompt";
import DemoModeBanner from "@/components/DemoModeBanner";
import OnboardingTour from "@/components/OnboardingTour";
import BottomNavBar from "@/components/BottomNavBar";
import SplashScreen from "@/components/SplashScreen";
import useSplashScreen from "@/hooks/useSplashScreen";
import { useServiceWorkerUpdate } from "@/hooks/useServiceWorkerUpdate";
import RequireAuth from "@/components/RequireAuth";

// Critical routes
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/DashboardCohesive";

// Lazy-loaded routes
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const CreateDrop = lazy(() => import("./pages/CreateDropCohesive"));
const DropDetail = lazy(() => import("./pages/DropDetail"));
const Install = lazy(() => import("./pages/Install"));
const Profile = lazy(() => import("./pages/Profile"));
const PublicProfileShare = lazy(() => import("./pages/PublicProfileShare"));
const Settings = lazy(() => import("./pages/Settings"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const InviteFlow = lazy(() => import("./pages/InviteFlow"));
const JoinDrop = lazy(() => import("./pages/JoinDrop"));
const MemoryVault = lazy(() => import("./pages/MemoryVault"));
const ContactsManager = lazy(() => import("./pages/ContactsManager"));
const NotificationCenter = lazy(() => import("./pages/NotificationCenter"));

const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminCron = lazy(() => import("./pages/AdminCron"));
const ExcuseLibrary = lazy(() => import("./pages/ExcuseLibrary"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const ExcuseHistory = lazy(() => import("./pages/ExcuseHistory"));

const Protected = ({ children }: { children: React.ReactNode }) => (
  <RequireAuth redirectToAuth>{children}</RequireAuth>
);

const DashboardScope = ({ children }: { children: React.ReactNode }) => (
  <DashboardDataProvider>{children}</DashboardDataProvider>
);

const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const isNativePlatform = (): boolean => {
  return typeof window !== "undefined" && !!(window as any).Capacitor?.isNativePlatform?.();
};

const AnimatedRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      if (target) target.scrollIntoView({ block: "start" });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search, location.hash]);

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<RouteLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
          <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
          <Route path="/auth/callback" element={<PageTransition><AuthCallback /></PageTransition>} />
          <Route path="/verify-email" element={<PageTransition><VerifyEmail /></PageTransition>} />
          <Route path="/dashboard" element={<Protected><DashboardScope><PageTransition><Dashboard /></PageTransition></DashboardScope></Protected>} />

          <Route path="/create" element={<Protected><PageTransition><CreateDrop /></PageTransition></Protected>} />
          <Route path="/create-event" element={<Navigate to="/create" replace />} />
          <Route path="/fun-zone" element={<Navigate to="/excuses" replace />} />
          <Route path="/drop/:id" element={<Protected><PageTransition><DropDetail /></PageTransition></Protected>} />
          <Route path="/invite/:inviteCode" element={<PageTransition><InviteFlow /></PageTransition>} />
          <Route path="/join/:inviteCode" element={<PageTransition><JoinDrop /></PageTransition>} />
          <Route path="/settings" element={<Protected><PageTransition><Settings /></PageTransition></Protected>} />
          <Route path="/memory-vault" element={<Protected><PageTransition><MemoryVault /></PageTransition></Protected>} />

          <Route path="/install" element={<PageTransition><Install /></PageTransition>} />
          <Route path="/profile" element={<Protected><DashboardScope><PageTransition><Profile /></PageTransition></DashboardScope></Protected>} />
          <Route path="/share/:userId" element={<PageTransition><PublicProfileShare /></PageTransition>} />
          <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/terms" element={<PageTransition><TermsOfService /></PageTransition>} />
          <Route path="/contacts" element={<Protected><PageTransition><ContactsManager /></PageTransition></Protected>} />
          <Route path="/notifications" element={<Protected><PageTransition><NotificationCenter /></PageTransition></Protected>} />
          <Route path="/plans" element={<Navigate to="/pricing" replace />} />
          <Route path="/payment-success" element={<Protected><PageTransition><PaymentSuccess /></PageTransition></Protected>} />
          <Route path="/admin/cron" element={<Protected><PageTransition><AdminCron /></PageTransition></Protected>} />

          <Route path="/excuses" element={<PageTransition><ExcuseLibrary /></PageTransition>} />
          <Route path="/excuse-history" element={<Protected><PageTransition><ExcuseHistory /></PageTransition></Protected>} />
          <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
          <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
          <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
          <Route path="/pricing" element={<PageTransition><Pricing /></PageTransition>} />

          <Route path="/.lovable/oauth/consent" element={<PageTransition><OAuthConsent /></PageTransition>} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const SPLASH_SESSION_KEY = "bb_splash_shown";

const shouldShowWebSplash = () => {
  if (typeof window === "undefined") return false;
  if (window.location.pathname !== "/") return false;
  try {
    if (sessionStorage.getItem(SPLASH_SESSION_KEY) === "1") return false;
    sessionStorage.setItem(SPLASH_SESSION_KEY, "1");
  } catch {
    /* private mode — fall through and show once */
  }
  return true;
};

const AppContent = () => {
  const [showSplash, setShowSplash] = useState(shouldShowWebSplash);
  const [showTour, setShowTour] = useState(false);
  const isNative = isNativePlatform();

  useSplashScreen();
  useServiceWorkerUpdate();

  useEffect(() => {
    if (isNative) setShowSplash(false);
  }, [isNative]);

  const handleStartTour = useCallback(() => setShowTour(true), []);
  const handleCloseTour = useCallback(() => setShowTour(false), []);

  return (
    <BrowserRouter>
      <TooltipProvider>
        <div className="min-h-screen">
          {showSplash && !isNative && (
            <SplashScreen duration={1400} onComplete={() => setShowSplash(false)} />
          )}
          <Toaster />
          <Sonner />
          <DemoModeBanner onStartTour={handleStartTour} />
          <main className="pt-0 has-[.demo-banner]:pt-10 pb-16 md:pb-0">
            <AnimatedRoutes />
          </main>
          <BottomNavBar />
          <SmartInstallPrompt />
          <OnboardingTour isOpen={showTour} onClose={handleCloseTour} />
        </div>
      </TooltipProvider>
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SoundProvider>
          <AuthSessionProvider>
            <PremiumProvider>
              <TierProvider>
                <AppContent />
              </TierProvider>
            </PremiumProvider>
          </AuthSessionProvider>
        </SoundProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
